import './style.css';

import { Button } from '@zinc/ui';

import { action, computed, isObservableArray, toJS } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { v4 as uuid } from 'uuid';
import Explanation from './components/explanation';
import Label from './components/label';
import OtherRadioButton from './components/other';
import RadioButtonListItem from './components/radio-button-list-item';
import { RBViewMode } from './enums';
import type { RadioButtonGroupProps } from './interfaces';

@observer
class RadioButtonGroup extends React.Component<RadioButtonGroupProps> {
  static defaultProps: Partial<RadioButtonGroupProps> = {
    displayName: 'name',
    errored: false,
    lookups: {},
    lookupValueMember: 'name',
    readOnly: false,
    vertical: false,
    value: [],
    valueMember: 'value'
  };

  protected readonly groupId = uuid();

  get lookuplist(): any[] {
    const { lookups, lookuplist, lookupValueMember } = this.props;

    if (typeof lookuplist === 'string') {
      if (Array.isArray(lookups) || isObservableArray(lookups)) {
        const lookup = lookups.find((l) => {
          return l[lookupValueMember] === lookuplist;
        });

        return lookup?.values ?? [];
      }

      const lookup = lookups[lookuplist];

      return lookup?.values ?? [];
    }

    return lookuplist ?? [];
  }

  get classNames(): string[] {
    const { errored, inline, list, value, warning } = this.props;

    const classNames = ['radio-button-group'];
    if (errored) {
      classNames.push('error');
      return classNames;
    }

    if ((value?.length ?? 0) == 0 && warning) {
      classNames.push('warning');
    }

    if (!!inline) {
      classNames.push('inline');
    }

    if (!!list) {
      classNames.push('list');
    }

    return classNames;
  }

  get className(): string {
    return this.classNames.join(' ');
  }

  get value(): string[] {
    let val = this.props.value || [];
    try {
      val = JSON.parse(val);
      val = val.map((v) => v.toString());
    } catch (ex) {
      if (typeof val === 'string') {
        val = this.arrayify(val);
      } else {
        val = [];
      }
    }

    return toJS(val);
  }

  getItemDetails(val: any) {
    // need to deal with item values etc
    let itemText, itemValue;
    if (typeof val === 'object') {
      itemText = val.text;
      itemValue = val.value || val.text;
    } else {
      itemText = val;
      itemValue = val;
    }
    return { itemText, itemValue };
  }

  @computed
  get otherActive(): boolean {
    return this.value.includes('Other');
  }

  @computed
  get listValues() {
    return (this.lookuplist || []).map((val) => {
      const details = this.getItemDetails(val);
      const active = this.value.includes(details.itemValue);

      return { ...details, active };
    });
  }

  @computed
  get viewMode(): RBViewMode {
    const { list, multi } = this.props;
    if (list) {
      return multi ? RBViewMode.checkbox : RBViewMode.radioButton;
    }
    return RBViewMode.button;
  }

  arrayify(value: string): string[] {
    const regex = /(\[|\])/gim;
    const splitRegex = /\,\s?/;
    const valueString = value.replace(regex, '');
    const valueArray = valueString.split(splitRegex);

    return valueArray.map((v) => v.toString());
  }

  @action.bound
  onChange(itemValue: string): void {
    const { multi, onChange } = this.props;
    const { value } = this;

    let val: string[];
    if (!multi) {
      val = [itemValue];
    } else {
      const idx = value.findIndex((v) => {
        return v === itemValue;
      });

      if (idx > -1) {
        value.splice(idx, 1);
      } else {
        value.push(itemValue);
      }
      val = value;
    }

    const result = val.length > 0 ? JSON.stringify(val) : null;

    return onChange.call(this, result);
  }

  render() {
    const { className, listValues, onChange, otherActive, viewMode } = this;
    const { readOnly, vertical, other, label, inline, valueOther, onChangeOther } = this.props;

    let readonlyNoLookup: JSX.Element[] | null = null;

    if (listValues.length === 0) {
      if (!readOnly) {
        return <div>No Item Source</div>;
      }

      readonlyNoLookup = this.value.map((x, idx) => {
        return (
          <span key={idx} className="ml-2">
            {x}
          </span>
        );
      });
    }

    const items = listValues.map((val, idx) => {
      const { itemText, itemValue, active } = val;
      return (
        <RadioButtonListItem
          key={idx}
          onChange={this.onChange}
          text={itemText}
          value={itemValue}
          active={active}
          viewMode={viewMode}
          readOnly={readOnly}
          name={!label || label.isEmptyOrWhitespace() ? `RB-${this.groupId}-${idx}` : label}
        />
      );
    });

    return (
      <span className="bz-radio-button-group">
        <Label label={label} inline={inline} />
        {readonlyNoLookup}
        <Button.Group className={className}>
          {items}
          <OtherRadioButton
            active={otherActive}
            other={other}
            readOnly={readOnly}
            viewMode={viewMode}
            onChange={onChange}
            name={!label || label.isEmptyOrWhitespace() ? `ORB-${this.groupId}` : label}
          />
        </Button.Group>
        <Explanation
          active={otherActive}
          inline={inline}
          other={other}
          valueOther={valueOther}
          onChangeOther={onChangeOther}
        />
      </span>
    );
  }
}

export default RadioButtonGroup;
